import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { AuthService } from '@instaclause-webapp/shared-core';
import {
  ButtonComponent,
  PasswordFieldComponent,
  ToastComponent,
  ToastService,
  ToastType,
} from '@instaclause-webapp/shared-ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, of } from 'rxjs';

import { MenuComponent } from '../../ui/menu/menu.component';

type ResetError = 'EXPIRED' | 'INVALID' | 'WEAK' | 'MISMATCH' | 'GENERIC';

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    MenuComponent,
    RouterModule,
    TranslocoModule,
    ReactiveFormsModule,
    PasswordFieldComponent,
    ButtonComponent,
    ToastComponent,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
  isSubmitting = false;
  error = signal<ResetError | null>(null);
  form = inject(FormBuilder).group({
    password: [ '', [ Validators.required, Validators.minLength(6) ] ],
    confirmPassword: [ '', Validators.required ],
  });

  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // Both come from the email link; without them the link is unusable.
  private code = this.route.snapshot.queryParamMap.get('code');
  private tenantId = this.route.snapshot.queryParamMap.get('tenantId');

  get linkInvalid(): boolean {
    return !this.code || !this.tenantId;
  }

  async submit() {
    const { code, tenantId } = this;
    if (this.isSubmitting || !code || !tenantId) return;

    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }

    const { password, confirmPassword } = this.form.value;
    if (!password || !confirmPassword) return;

    if (password !== confirmPassword) {
      this.error.set('MISMATCH');

      return;
    }

    this.error.set(null);
    this.isSubmitting = true;
    try {
      const result = await firstValueFrom(
        this.authService.confirmPasswordReset(tenantId, code, password).pipe(
          catchError((httpError: HttpErrorResponse) => {
            this.error.set(this.mapError(httpError));

            return of('failed' as const);
          })
        )
      );

      if (result !== 'failed') {
        this.toastService.showToast({
          type: ToastType.Success,
          text: this.translocoService.translate('RESET_PASSWORD.SUCCESS'),
          duration: 5,
          testId: 'reset-password-success-toast',
        });
        this.router.navigate([ '/login' ]);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private mapError(httpError: HttpErrorResponse): ResetError {
    switch (httpError.error?.title) {
      case 'Auth.ResetCodeExpired':
        return 'EXPIRED';
      case 'Auth.ResetCodeInvalid':
        return 'INVALID';
      case 'Auth.WeakPassword':
        return 'WEAK';
      default:
        return 'GENERIC';
    }
  }
}
